import {parseOrNull} from "../../utils";
import {Query} from "../ApiProvider/types";
import {makeQuery} from "../hooks";
import {
    CallLogs,
    ParticulateMatter10Filter,
    RawParticulateMatter10Filter,
    RawSite,
    ReducedParticulateMatter10Filter, ReducedRawParticulateMatter10Filter,
    SimpleCallLogs, SimpleCdv, SimpleIdv, Location, SimpleLocation, TrainsSummary,
    HDMine,SimpleTrain,TrainDetails
} from "./types";


export const pm10FilterAll: Query<Pick<RawParticulateMatter10Filter,
    "id" | "exposureOn" | "locationId" | "concentration">[]> = {
    xFields: ["id", "locationId", "exposureOn", "concentration"],
    path: "/api/pm10_filters",
};


export const pm10FilterByDateRange: Query<Pick<RawParticulateMatter10Filter,
    "id" | "exposureOn" | "locationId" | "concentration">[]> = {
    xFields: ["id", "locationId", "exposureOn", "concentration"],
    path: "/api/locations/<locationId>/pm10_filters/<fromDate>/<toDate>",
};


export const pm10FilterByMiddleDate: Query<Pick<RawParticulateMatter10Filter,
    "id" | "exposureOn" | "locationId" | "concentration">[]> = {
    xFields: ["id", "locationId", "exposureOn", "concentration"],
    path: "/api/locations/<locationId>/pm10_filters/<middleDate>/limit/<limit>",
};


export const pm10FilterById: Query<RawParticulateMatter10Filter> = {
    xFields: [
        "concentration",
        "exposureOn",
        "locationId",
        "preWeight",
        "postWeight",
        "flowRate",
        "runTime",
        "postSiteTemp",
        "precipitation",
        "postSitePressure",
        "comments"
    ],
    path: "/api/pm10_filters/<id>",
};


export type AllSites_Site = Pick<RawSite, "id" | "name">;
export type AllLocations_Location = Pick<RawSite, "id" | "name" | "longitude" | "latitude">;
export type AllLocations_Tsm = Pick<RawSite, "id" | "name">;


export const sitesAll: Query<AllSites_Site[]> = {
    xFields: ["id", "name"],
    path: "/api/sites",
};

export const locationsAll: Query<AllLocations_Location[]> = {
    xFields: ["id", "name", "latitude", "longitude"],
    path: "/api/sites/1/locations", // Temporarily using sites 1 to fetch locations
};

export const locationsTsm: Query<AllLocations_Tsm[]> = {
    xFields: ["id", "name"],
    path: "/api/locations-tsm", // The API is using site id != 1 to get tsm locations
};

export const locationsAllUnfiltered: Query<AllLocations_Location[]> = {
    xFields: ["id", "name", "latitude", "longitude"],
    path: "/api/locations", // Temporarily using sites 1 to fetch locations
};

export const locationById: Query<Location> = {
    xFields: ["id","name"],
    path: "/api/locations/<id>",
};

export const callLogsAll: Query<SimpleCallLogs[]> = {
    xFields: ["callerCity", "callerState", "calledAt", "id"],
    path: "/api/call_logs",
};

export const callLogsById: Query<CallLogs> = {
    xFields: [],
    path: "/api/call_logs/<id>",
};

export const callLogsByDate: Query<SimpleCallLogs[]> = {
    xFields: ["callerCity", "callerState", "calledAt", "id"],
    path: "/api/call_logs/by_year/<date>",
};


export const cdvsByDate: Query<SimpleCdv[]> = {
    xFields: ["name", "cdv", "id"],
    path: "/api/locations/<id>/cdvs/<date>",
};

export const useAllCdvs = makeQuery(cdvsByDate);
export const useAllSites = makeQuery(sitesAll);
export const useAllLocations = makeQuery(locationsAll);
export const useAllUnfilteredLocations = makeQuery(locationsAllUnfiltered);
export const useAllCallLogs = makeQuery(callLogsAll);
export const useCallLogsById = makeQuery(callLogsById);
export const useCallLogsByDate = makeQuery(callLogsByDate);

export const makeReducedPM10FiltersQuery = (query: Query<ReducedRawParticulateMatter10Filter[]>) =>
    makeQuery<ReducedRawParticulateMatter10Filter[],
        ReducedParticulateMatter10Filter[]>(query, (data) => {
        return data?.filter(x => !(x.exposureOn === null && x.locationId === null)).map(x => {
            return {
                ...x,
                concentration: parseOrNull(x.concentration),
            };
        }) ?? null;
    });


export const usePM10FiltersByMiddleDate = makeReducedPM10FiltersQuery(pm10FilterByMiddleDate);


export const usePM10FilterById = makeQuery<RawParticulateMatter10Filter,
    ParticulateMatter10Filter>(
    pm10FilterById,
    (data) => {
        if (data) {
            return {
                id: data.id,
                exposureOn: data.exposureOn,
                locationId: data.locationId,
                concentration: parseOrNull(data.concentration),
                preWeight: parseOrNull(data.preWeight),
                postWeight: parseOrNull(data.postWeight),

                flowRate: parseOrNull(data.flowRate),
                runTime: parseOrNull(data.runTime),
                postSiteTemp: parseOrNull(data.postSiteTemp),
                precipitation: parseOrNull(data.precipitation),
                postSitePressure: parseOrNull(data.postSitePressure),
                comments: data.comments,
            };
        }
        return null;
    });

export const useLocationById = makeQuery<SimpleLocation,Location>(
    locationById,
    (data) => {
	if (data) {
	    return {
		id: data.id,
		name: data.name,
	    };
	}
	return null;
    });

export const idvsExceededByDate: Query<SimpleIdv[]> = {
    xFields: ["idv","id"],
    path: "/api/idvs/exceeded/<location_id>/<start_date>/<end_date>",
};

export const useIdvsExceededByDate = makeQuery(idvsExceededByDate);

export const idvsExceededByPeriod: Query<SimpleIdv[]> = {
    xFields: ["idv","id"],
    path: "/api/idvs/exceeded/<location_id>/<period_date>",
};

export const useIdvsExceededByPeriod = makeQuery(idvsExceededByPeriod);

export const idvsByPeriod: Query<SimpleIdv[]> = {
    xFields: ["idv","id"],
    path: "/api/locations/<id>/idvs/<period_date>",
};

export const useIdvsByPeriod = makeQuery(idvsByPeriod);

export const trainsSummaryByPeriod: Query<TrainsSummary> = {
    xFields: ["count","avg_speed","empty_count","coal_count","freight_count"],
    path: "/api/locations/<id>/trains_summary/<period_date>",
};

export const useTrainsSummaryByPeriod = makeQuery(trainsSummaryByPeriod);

export const hdMinesByPeriod: Query<HDMine[]> = {
    xFields: ["name","mine_cd","frequency","total"],
    path: "/api/locations/<id>/hd_mines/<period_date>/<limit>",
};

export const useHDMinesByPeriod = makeQuery(hdMinesByPeriod);

export const alertTrainsByPeriod: Query<SimpleTrain[]> = {
    xFields: ["id","name","start_at","avg_speed","met_train"],
    path: "/api/locations/<id>/alert_trains/<period_date>",
};

export const useAlertTrainsByPeriod = makeQuery(alertTrainsByPeriod);

export const trainsByPeriod: Query<SimpleTrain[]> = {
    xFields: ["id","name","start_at","avg_speed","idv","met_train","type"],
    path: "/api/locations/<id>/trains/<period_date>",
};

export const useTrainsByPeriod = makeQuery(trainsByPeriod);

// ram_1_graph/ram_2_graph are base64 png data urls and add roughly 27KB each
// to the response. The api only marshals them because they are listed here --
// X-Fields masking means an unrequested graph field is never even read from
// the database -- so keep them off any query that returns more than one train.
export const trainById: Query<TrainDetails> = {
    xFields: ["id","name","start_at","avg_speed","cat","associated_mines","stcc_codes","coal_car_count","loaded_car_count","empty_car_count","idv","ram_1_graph","ram_2_graph"],
    path: "/api/trains/<id>-<location_id>",
};

export const useTrainById = makeQuery(trainById);
